import React, { useState, useEffect } from 'react';
import { useAlert } from './AlertContext';
import {
  CustomInput,
  CustomSelect,
  CustomSlider,
  CustomTextarea,
  CustomToggle,
} from './CustomFormElements';
import Button from './Buttons';
import { useChannelApi } from '../Services/ChannelApiProvider';
import { isEmpty } from '../utils/validationUtil';
import { useAuth } from '../Contexts/AuthContext';

function TriggerSettings({ channelId, canEdit, initialData, onClose, onUpdate, onUpdateTrigger }) {
  const { showAlert } = useAlert();
  const { status } = useAuth();
  const { createTrigger, updateTrigger, getThematics, getNotifications } = useChannelApi();
  const isNew = !initialData.id;

  const [triggerPhrase, setTriggerPhrase] = useState('');
  const [replyPhrase, setReplyPhrase] = useState('');
  const [expression, setExpression] = useState('');
  const [action, setAction] = useState('');
  const [triggerType, setTriggerType] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [thematics, setThematics] = useState([]);
  const [confidenceLevel, setConfidenceLevel] = useState(50);
  const [triggerThematic, setTriggerThematic] = useState(null);
  const [triggerNotifications, setTriggerNotifications] = useState();
  const [notifications, setNotifications] = useState([]);

  const isNotificationFlag = status?.feature_flags?.NOTIFICATION === true;

  useEffect(() => {
    setTriggerPhrase(initialData.trigger_message || '');
    setAction(initialData.action || '');
    setTriggerType(initialData.trigger_type || '');
    setReplyPhrase(initialData.reply_phrase || '');
    setConfidenceLevel(initialData.confidence_level || 50);
    setExpression(initialData.expression || '');

    if (initialData.thematic_id && thematics.length > 0) {
      const thematicObj = thematics.find((t) => t.key === initialData.thematic_id);
      if (thematicObj) {
        setTriggerThematic(thematicObj.key);
      }
    }

    if (initialData.notification_id && notifications.length > 0) {
      const notificationObj = notifications.find((t) => t.id === initialData.notification_id);
      if (notificationObj) {
        setTriggerNotifications(notificationObj.id);
      }
    }

    setIsActive(initialData.is_active ?? true);
  }, [initialData, thematics, notifications]);

  useEffect(() => {
    const fetchThematics = async () => {
      try {
        const res = await getThematics();
        setThematics(res?.data || res || []);
      } catch (err) {
        console.error('Failed to fetch thematics:', err);
      }
    };

    const fetchNotifications = async () => {
      try {
        const res = await getNotifications();
        setNotifications(res?.items || res || []);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };

    fetchThematics();
    fetchNotifications();
  }, [getThematics, getNotifications]);

  const handleSave = async () => {
    if (isEmpty(triggerType)) {
      showAlert('fail', 'Please select Trigger Type', 'error');
      return;
    }
    if (isEmpty(action)) {
      showAlert('fail', 'Please select action', 'error');
      return;
    }
    const payload = {
      trigger_message: triggerPhrase,
      action: action,
      thematic_key: triggerThematic,
      is_active: isActive,
      trigger_type: triggerType,
      reply_phrase: replyPhrase,
      confidence_level: confidenceLevel,
      notification_key: triggerNotifications,
      expression: expression,
    };
    console.log('payload', payload);
    try {
      if (isNew) {
        await createTrigger(channelId, payload);
      } else {
        await updateTrigger(channelId, initialData.id, payload);
      }
      onUpdateTrigger();

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to save trigger:', error);
      showAlert('fail', 'Failed to save trigger.');
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <h2 className="h4-title">{isNew ? 'Create New Trigger' : 'Edit Trigger'}</h2>

      <div style={{ flexGrow: 1, paddingTop: '1rem' }}>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Trigger Type</div>
            <div className="small-font">Select trigger type</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="trigger-type"
              label="Trigger Type"
              options={[
                { value: 'start', label: 'Start' },
                { value: 'phrase', label: 'Trigger Phrase' },
                { value: 'thematic', label: 'Thematic' },
                { value: 'no_thematic', label: 'No Thematic' },
                { value: 'system', label: 'System' },
              ]}
              value={triggerType}
              onChange={(val) => setTriggerType(val)}
              multiSelect={false}
              disabled={!canEdit}
            />
          </div>
        </div>
        {triggerType === 'phrase' && (
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Trigger Phrase</div>
              <div className="small-font">Enter the phrase that will activate this trigger.</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="trigger-phrase"
                label="Trigger Phrase"
                placeholder="e.g., 'What's the weather?'"
                value={triggerPhrase}
                onChange={(id, value) => setTriggerPhrase(value)}
                disabled={!canEdit}
              />
            </div>
          </div>
        )}
        {triggerType === 'thematic' && (
          <>
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Thematic</div>
                <div className="small-font">Select thematic (Optional)</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id="thematic-select"
                  label="Thematic"
                  options={thematics.map((t) => ({
                    value: t.key,
                    label: t.name,
                  }))}
                  value={triggerThematic}
                  multiSelect={false}
                  onChange={(val) => setTriggerThematic(val)}
                  disabled={!canEdit}
                />
              </div>
            </div>
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Confidence Level</div>
                <div className="small-font">Set the confidence level (0-100)</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSlider
                  id="confidence-slider"
                  label="Confidence Level"
                  min={50}
                  max={100}
                  oneStep={5}
                  suffix=" %"
                  value={confidenceLevel}
                  onChange={(val) => setConfidenceLevel(val)}
                  disabled={false}
                />
              </div>
            </div>
          </>
        )}
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Action</div>
            <div className="small-font">Select action</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="action"
              label="Action"
              options={[
                { value: 'moderate', label: 'Moderate' },
                { value: 'moderate_off', label: 'Moderate Off' },
                { value: 'ignore', label: 'Ignore' },
                { value: 'skip', label: 'Skip' },
                { value: 'deactivate', label: 'Deactivate' },
                { value: 'deactivate_forever', label: 'Deactivate Forever' },
                { value: 'close', label: 'Close' },
                ...(isNotificationFlag ? [{ value: 'forward', label: 'Forward' }] : []),
                { value: 'reply_with', label: 'Reply With' },
              ]}
              value={action}
              onChange={(val) => setAction(val)}
              multiSelect={false}
              disabled={!canEdit}
            />
          </div>
        </div>
        {action === 'reply_with' && (
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Reply Phrase</div>
              <div className="small-font">Reply phrase</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="reply-phrase"
                label="Reply Phrase"
                placeholder="e.g., 'What's the weather?'"
                value={replyPhrase}
                onChange={(id, value) => setReplyPhrase(value)}
                disabled={!canEdit}
              />
            </div>
          </div>
        )}

        {action === 'forward' && (
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Notifications</div>
              <div className="small-font">Select notifications</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="notifications"
                label="Notifications"
                options={(notifications || []).map((a) => ({
                  value: a.id,
                  label: a.name,
                }))}
                value={triggerNotifications}
                multiSelect={false}
                onChange={(val) => setTriggerNotifications(val)}
                disabled={!canEdit || !isNotificationFlag}
              />
            </div>
          </div>
        )}
        {triggerType === 'system' && (
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Expression</div>
              <div className="small-font">Add trigger expression</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="expression"
                label="Expression"
                name="expression"
                type="text"
                addedText={expression}
                onChange={(value) => setExpression(value)}
                resizable={false}
                height="200px"
              />
            </div>
          </div>
        )}
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Activate Trigger</div>
            <div className="small-font">
              Set this trigger to be active or inactive to enable or disable it.
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomToggle
              id="toggle-status"
              label={isActive ? 'Active' : 'Inactive'}
              checked={isActive}
              onChange={(id, value) => setIsActive(value)}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>

      <div className="buttons-container d-flex justify-end">
        {canEdit && (
          <Button variant="primary" onClick={handleSave}>
            {isNew ? 'Create Trigger' : 'Save Changes'}
          </Button>
        )}
        <Button variant="danger" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default TriggerSettings;
